import type { AdminOrderPaymentDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';
import { formatCurrency } from '../../../utils/format';

/**
 * AUT-219 — the two money figures, side by side, each with its own label.
 *
 * **This is the point of the ticket.** Under two-stage, `amount` is what was
 * BLOCKED on the customer's card and `capturedAmount` is what was actually
 * TAKEN. They differ on every partial settle, and the panel that renders the
 * first one under the word "списано" puts a backend bug on screen. So: two
 * boxes, two labels, never one number doing both jobs.
 *
 * Before a capture, `capturedAmount` is `null` and the box says so in words
 * ("ещё не списано") rather than showing a bare em-dash that could be read as
 * "zero" or as "unknown".
 *
 * The authorized-side label is conditional: on a legacy single-stage payment
 * there was never a hold, so calling its amount "заблокировано на карте" would
 * be a lie. `authorizedAt !== null` is the discriminator.
 *
 * Currency comes from the DTO (`payment.currency`), never a hardcoded symbol —
 * AUT-166 and AUT-179 were both regressions where USD leaked onto a money
 * surface because a component decided the sign for itself.
 */
const AmountBox = ({
  label,
  value,
  emphasis,
  testId,
}: {
  label: string;
  value: string;
  /** `strong` tints the figure green — reserved for money we actually took. */
  emphasis: 'strong' | 'neutral' | 'muted';
  testId: string;
}): ReactElement => {
  const color =
    emphasis === 'strong'
      ? tokens.state.success
      : emphasis === 'muted'
        ? tokens.text.tertiary
        : tokens.text.primary;
  return (
    <Box
      data-testid={testId}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.75,
        bgcolor: 'background.paper',
      }}
    >
      <Typography sx={{ fontSize: 11, color: tokens.text.tertiary, mb: 0.5 }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: emphasis === 'muted' ? 15 : 22,
          fontWeight: emphasis === 'muted' ? 500 : 700,
          letterSpacing: '-0.4px',
          color,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export const PaymentAmounts = ({ payment }: { payment: AdminOrderPaymentDto }): ReactElement => {
  const { t } = useTranslation();
  const twoStage = payment.authorizedAt !== null;
  const captured = payment.capturedAmount;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px' }}>
      <AmountBox
        testId="payment-authorized-amount"
        label={
          twoStage
            ? t('orders.detail.payment.authorizedAmount')
            : t('orders.detail.payment.chargedAmount')
        }
        value={formatCurrency(Number(payment.amount), payment.currency)}
        emphasis="neutral"
      />
      <AmountBox
        testId="payment-captured-amount"
        label={t('orders.detail.payment.capturedAmount')}
        value={
          captured === null
            ? t('orders.detail.payment.notCapturedYet')
            : formatCurrency(Number(captured), payment.currency)
        }
        emphasis={captured === null ? 'muted' : 'strong'}
      />
    </Box>
  );
};
